@use '@/styles/tokens' as *;

.body {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

/* Header cluster, rendered inside the drawer's cream title band (Figma 327:283632). Figma sets 14px
   between the title group and the chip and 10px between badge and title; neither has a token, so one
   flat 12px is used — the value the shipped sibling ships. */
.header {
  display: inline-flex;
  align-items: center;
  gap: var(--space-3);
}

.iconBadge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 32px; // Figma 327:283635 — fixed 32×32 badge, no size token
  height: 32px;
  border-radius: $radius-pill;
  background: $color-accent1-900; // Figma Accent 1/accent-900 #656B25
  color: var(--text-inverse);
}

.headerHeading {
  font-size: 18px; // Main Font-Geist/18/M — no $font-size-* token at 18
  font-weight: $font-weight-medium;
  color: $color-neutral-900;
}

/* Device name + category (Figma 327:283580) */
.section {
  display: flex;
  flex-direction: column;
  gap: 2px; // Figma — below the 4px token floor
  margin-bottom: var(--space-3); // Figma 10px — no token; nearest, and the sibling's value
}

.name {
  font-size: $font-size-lg;
  font-weight: $font-weight-medium;
  color: var(--text-primary);
  overflow-wrap: break-word; // wraps, never truncates
}

.category {
  font-size: $font-size-xs;
  color: var(--text-muted);
}

/* Evidence thumbnails (Figma 327:283584).
   A fixed 4-column grid, NOT flex-wrap: the AC requires "four to a row, with a fifth wrapping onto
   the next row", and flex-wrap only achieves that by arithmetic luck. The drawer body is 416px wide
   (464 - 2×24 chrome padding) and 4×92 + 3×17.25 = 419.75 — negative slack, so the `overflow-y:auto`
   body plus a classic space-consuming scrollbar wraps the FOURTH tile and renders 3+2. Four explicit
   columns guarantee the layout at any body width; at 416 each cell computes to ~92px, and under a
   scrollbar the tiles shrink instead of wrapping. */
.thumbGrid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--space-4); // Figma 17.25px — no token; 16px is nearest
  margin: 0 0 var(--space-4); // Figma 327:283584 → 327:283597 = 16px
  padding: 0;
  list-style: none;
}

.thumbBtn {
  width: 100%; // grid cell owns the width — 92px at the drawer's 464px (Figma 327:283585)
  aspect-ratio: 1; // keeps the Figma square as the cell flexes
  padding: 0;
  border: none; // Figma draws no border and no radius on these tiles
  background: $color-neutral-100; // Figma #F6F8F7 — nearest token
  overflow: hidden;
  cursor: pointer;

  &:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }
}

.thumbImg {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Reason + description (Figma 327:283597): white card, subtle border, 10px radius. The bottom
   margin is where ticket 05's Activity Timeline heading lands (Figma 431 → 447 = 16px). */
.reasonCard {
  display: flex;
  flex-direction: column;
  gap: var(--space-3); // Figma 327:283598 — 12px
  margin: 0 0 var(--space-4);
  padding: 19px 17px; // Figma 327:283597 — neither pad has a --space token
  background: var(--surface-card);
  border: 1px solid var(--border-subtle);
  border-radius: 10px; // Figma — between $radius-lg (8) and $radius-xl (12)
}

.reasonHeading {
  margin: 0;
  font-size: 15px; // Main Font-Geist/15/M — no $font-size-* token at 15
  font-weight: $font-weight-medium;
  color: var(--text-tertiary);
}

.description {
  margin: 0;
  font-size: $font-size-md;
  line-height: 1.5;
  color: var(--text-tertiary);
  overflow-wrap: break-word; // AC: the complete description, wrapped rather than truncated
}

/* Activity Timeline heading (Figma 429:311571 / 429:311593: Geist 14/R, raw #6A7282 with no Figma
   variable — $color-neutral-600 (#777777) is the nearest token and the value the shipped twin
   ships, which keeps the two sibling drawers identical). */
.timelineHeading {
  margin: 0 0 var(--space-6); // Figma 24px, heading → first entry
  font-size: $font-size-md;
  font-weight: $font-weight-regular;
  color: $color-neutral-600;
}

/* Rejected: the outcome block below the timeline (Figma 327:285087) — inverted from the queue twin,
   where it sits above. The shell is Figma's; the copy is the AC's, because Figma's "Request
   Rejected" heading and installation-fee body are lifted from the installation flow (ADR-0023). */
.rejection {
  display: flex;
  flex-direction: column;
  gap: 6px; // Figma — below the 8px token step
  margin: 0;
  padding: var(--space-3); // Figma 12px
  background: $color-error-50; // Figma #FFF6F6
  border-radius: $radius-xl; // Figma 12px
}

.rejectionHeading {
  display: inline-flex;
  align-items: center;
  gap: 6px; // Figma 327:285088
  font-size: $font-size-md;
  font-weight: $font-weight-regular;
  color: $color-stage-attention-text; // Figma #A2191F
}

.rejectionValue {
  margin: 0;
  font-size: $font-size-xs;
  color: $color-error-950; // Figma #560D10
  overflow-wrap: break-word;
}

// Failure copy is centred in the body, matching the listing drawer. Never a fixed padding-block —
// Figma centres states it didn't draw, and a top-offset state was a real review defect on the
// sibling's ticket 03.
.notice {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  margin: 0;
  text-align: center;
  color: var(--text-primary);
}
